<template>
  <div class="GraphContainer">
    <div v-if="barChartData.length > 0">
      <div class="subHeader1">
        {{ channelUpCount }} of {{ totalChannelCount }} Channels are Up
      </div>
      <DxChart
        id="chart"
        ref="chartRef"
        :data-source="barChartData"
        :rotated="true"
        :palette="getCustomPalette"
        :bar-group-width="40"
        :size="{ height: 250 }"
        @series-click="openDMAPopup"
      >
        <DxCommonSeriesSettings
          :ignore-empty-points="true"
          argument-field="Status"
          value-field="StatusCount"
          type="bar"
          :minBarSize="3"
        >
          <DxLabel
            :visible="true"
            position="outside"
            backgroundColor="transparent"
            :customize-text="customizeText"
          />
        </DxCommonSeriesSettings>
        <DxCommonAxisSettings>
          <DxLabel :visible="false" />
        </DxCommonAxisSettings>
        <DxValueAxis :visual-range="chart_visualRange">
          <DxLabel :visible="true">
            <DxFont :size="12" color="#363636" :weight="700" />
          </DxLabel>
        </DxValueAxis>
        <DxSeriesTemplate
          name-field="Status"
          :customize-series="customizeSeries"
        />
        <DxLegend
          vertical-alignment="bottom"
          horizontal-alignment="right"
          orientation="horizontal"
          item-text-position="right"
        />
      </DxChart>
    </div>
    <div v-else class="NoDataHeader">
      <span v-if="!isNielsenCompany && !isDataLoading">
        No Live Channels to Display <br />See Channels List Page for More
        Information
      </span>
    </div>
    <div>
      <customModal
        id="dmaPopup"
        :title="
          `Channel Status ` + `${this.title != undefined ? this.title : ''}`
        "
        :show.sync="showChannelsStatusPopup"
        :centered="true"
        :backdrop="true"
        :closeOnBackdrop="false"
        width="70%"
        :CCRColoBrand="true"
      >
        <NonCCRChannelsStatusGrid
          v-if="showChannelsStatusPopup"
          :dmaID="dmaID"
          :dmaNameAndRank="dmaNameAndRank"
          :barClicked="barClicked"
          :chartLocation="chartLocation"
          :channelStatusPopupData="channelStatusPopupData"
        />
      </customModal>
    </div>
  </div>
</template>

<script>
import NonCCRChannelsStatusGrid from "../ChannelStatusModal/NonCCRChannelsStatusGrid.vue";
import { EventBus } from "../../../../components/Events";
import {
  DxChart,
  DxSeriesTemplate,
  DxCommonSeriesSettings,
  DxLegend,
  DxLabel,
  DxFont,
  DxValueAxis,
  DxCommonAxisSettings,
} from "devextreme-vue/chart";

export default {
  name: "NonCCRChannelStatusChart",
  components: {
    DxChart,
    DxSeriesTemplate,
    DxCommonSeriesSettings,
    DxLegend,
    DxLabel,
    DxFont,
    DxValueAxis,
    DxCommonAxisSettings,
    NonCCRChannelsStatusGrid,
  },
  data() {
    return {
      user: this.$auth.user.UserDetails,
      barChartData: [],
      totalChannelCount: 0,
      channelUpCount: 0,
      showChannelsStatusPopup: false,
      chart_visualRange: [],
      barClicked: undefined,
      rangeBuffer: 0,
      chartLocation: undefined,
      title: "",
      channelStatusPopupData: [],
      statusColors: {
        1: "#8BC549",
        2: "#E60000",
        3: "#626466",
      },
    };
  },
  props: {
    dmaID: Number,
    dmaNameForFilter: String,
    dmaNameAndRank: String,
    isDataLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  mounted: function () {
    this.barChartCounts();
    EventBus.$on("resize-chart", () => {
      this.resizeChart();
    });
  },
  watch: {
    showChannelsStatusPopup: function () {
      if (!this.showChannelsStatusPopup) {
        this.chart_visualRange = [0, this.totalChannelCount + this.rangeBuffer];
      }
    },
  },
  methods: {
    resizeChart() {
      setTimeout(() => {
        this.$refs["chartRef"]?.instance.render();
      }, 500);
    },
    customizeSeries(e) {
      return { color: this.barChartData.find((c) => c.Status == e).color };
    },
    openDMAPopup(e) {
      var series = e.target._data[0].data.Status;
      if (series != "All") {
        this.barClicked = series;
      }
      if (this.dmaID != undefined && this.dmaID != null) {
        this.chartLocation = "dashboard";
        this.title = `in ${this.dmaNameAndRank}`;
        this.showChannelsStatusPopup = true;
      } else {
        this.chartLocation = "home";
        this.chart_visualRange = [0, this.totalChannelCount + this.rangeBuffer];
        this.$router.push({
          name: "Channels List",
          params: {
            StatusID: e.target._data[0].data.StatusID,
            ProductPhase_ID: 2,
          },
        });
      }
    },
    customizeText({ valueText }) {
      return `<div style="color:grey;font-weight:bold;">${valueText}</div>`;
    },
    barChartCounts() {
      this.$emit("status-data-loaded", false);
      let dmaFilter = "";
      if (this.dmaID) {
        dmaFilter = "?MarketID=" + this.dmaID;
      }
      let url = "https://api.ccr.net/ColoM360/ChannelStatusCounts";
      if (this.dmaID) {
        url += dmaFilter;
      }
      this.$axios.get(url).then((response) => {
        let chartData = response.data;
        if (chartData.length) {
          this.barChartData = chartData
            .map((m) => {
              return {
                ...m,
                color: this.statusColors[m.StatusID],
              };
            })
            .sort((a, b) => (a.StatusID > b.StatusID ? -1 : 1));
          this.totalChannelCount = chartData.reduce((sum, status) => {
            return sum + status.StatusCount;
          }, 0);
          this.channelUpCount =
            chartData.find((f) => f.StatusID == 1)?.StatusCount ?? 0;
          this.chart_visualRange = [0, this.totalChannelCount];
          this.channelStatusPopupData = chartData.map((m) => ({
            type: m.Status,
            count: m.StatusCount,
          }));
          this.channelStatusPopupData.push({
            type: "All",
            count: this.totalChannelCount,
          });
        }
        // //setting visual range proportionate to max count
        this.rangeBuffer = parseInt(this.totalChannelCount * (5 / 100));
        if (this.rangeBuffer < 1) {
          this.rangeBuffer = 1;
        }
        //When total count and channels up is the same.
        if (this.totalChannelCount == this.channelUpCount) {
          this.chart_visualRange = [
            0,
            this.totalChannelCount + this.rangeBuffer,
          ];
        }
        this.$emit("status-data-loaded", true);
      });
    },
  },
  computed: {
    isNielsenCompany() {
      return this.user.Company_RecID == 20822;
    },
    getCustomPalette() {
      if (this.channelStatusPopupData.length > 3) {
        return ["#626466", "#E60000", "#8BC549"];
      }
      return ["#E60000", "#8BC549"];
    },
  },
};
</script>

<style lang="scss" scoped>
.GraphContainer::v-deep {
  .dxc-series {
    g {
      .dxc-strips-group {
        display: none;
      }
      rect {
        rx: 9;
        ry: 9;
        cursor: pointer;
        height: 25px;
      }
    }
  }
  .dxc-val-line,
  .dxc-arg-line {
    display: none;
  }
  text-align: center;
}
#chart {
  padding-top: 0px;
  margin-top: -10px;
  //height: 290px;
}
#chart::v-deep {
  .dxc-series {
    rect {
      cursor: pointer;
    }
  }
}
.subHeader1 {
  font-size: 16px;
  color: grey;
  float: left;
  font-weight: bold;
  padding: 15px 0 20px 0;
}
.NoDataHeader {
  font-size: 16px;
  font-weight: bold;
  color: grey;
  padding-bottom: 32px;
  padding-top: 32px;
}
</style>